import JSONbig from 'json-bigint'
import { API_BASE } from './config.js'

const jsonParser = JSONbig({ storeAsString: true })

function getToken() {
  return localStorage.getItem('token') || ''
}

function request(method, path, body) {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + getToken()
  }
  const options = {
    method,
    headers,
    credentials: 'include'
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  return fetch(`${API_BASE}${path}`, options)
    .then(res => res.text())
    .then(text => jsonParser.parse(text))
}

/**
 * 创建时间块/记录
 * @param {Object} params
 * @param {number} params.workspaceId 工作区 ID
 * @param {number} params.projectId 项目 ID
 * @param {number} [params.taskId] 任务 ID
 * @param {number} [params.tagId] 标签 ID
 * @param {string} [params.blockType] 类型：TASK / RECORD，记录传 RECORD
 * @param {string} params.plannedStartTime 计划开始时间（ISO 8601）
 * @param {string} params.plannedEndTime 计划结束时间（ISO 8601）
 * @param {string} [params.actualStartTime] 实际开始时间（ISO 8601）
 * @param {string} [params.actualEndTime] 实际结束时间（ISO 8601）
 * @param {string} [params.content] 记录内容
 * @param {string} [params.note] 备注
 * @param {string} [params.color] 颜色 #RRGGBB
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function createTimeBlock(params) {
  return request('POST', '/api/v1/timeline/blocks', params)
}

/**
 * 查询时间块区间列表
 * @param {Object} params
 * @param {number} params.workspaceId 工作区 ID
 * @param {number} params.projectId 项目 ID
 * @param {string} params.startDate 开始日期 yyyy-MM-dd
 * @param {string} params.endDate 结束日期 yyyy-MM-dd
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function getTimeBlocksRange(params) {
  const query = new URLSearchParams({
    workspaceId: String(params.workspaceId),
    projectId: String(params.projectId),
    startDate: params.startDate,
    endDate: params.endDate
  }).toString()
  return request('GET', `/api/v1/timeline/range?${query}`)
}

/**
 * 查询本人实时动态（轻量）。
 *
 * 只回「正在做 / 上一次做了什么」，不带回整张表的周数据；供时间表页在自己编辑后
 * 跳过整表静默重拉、仅刷新底部动态行时使用（与 /timeline/week 响应里的 activity 同口径）。
 *
 * @returns {Promise<{code:number,message:string,data:Object|null,success:boolean,timestamp:string}>}
 */
export function getOwnActivity() {
  return request('GET', '/api/v1/timeline/own-activity')
}

/**
 * 查询单个时间块详情。
 *
 * <p>跨天结束「在做」块时要用：提醒弹窗手里只有定时条目（title / catLabel / startedAt），
 * 没有 tagId / content / projectId，需要先把块的完整信息补出来才能给第二天建新块。</p>
 *
 * @param {string|number} id 时间块 ID
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function getTimeBlock(id) {
  return request('GET', `/api/v1/timeline/blocks/${id}`)
}

/**
 * 修改时间块
 * @param {string|number} id 时间块 ID
 * @param {Object} params
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function updateTimeBlock(id, params) {
  return request('PUT', `/api/v1/timeline/blocks/${id}`, params)
}

/**
 * 删除时间块
 * @param {string|number} id 时间块 ID
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function deleteTimeBlock(id) {
  return request('DELETE', `/api/v1/timeline/blocks/${id}`)
}

/**
 * 更新时间块执行状态（普通 / 正在做 / 预设 三种状态的流转）。
 *
 * - 预设（PLANNED）→ 勾选完成 → COMPLETED（后端按计划区间回填 actual，成为普通时间块）
 * - 正在做（RUNNING）→ 结束 → COMPLETED（后端把 actualEnd 设为当前时刻）
 *
 * @param {string|number} id 时间块 ID
 * @param {string} status 目标状态：PLANNED / RUNNING / COMPLETED
 * @param {string} [actualEndTime] 实际结束时间（ISO 8601，本地时刻）。
 *        仅 RUNNING → COMPLETED 生效：**跨天结束**「在做」块时传「当天 23:59:59」，
 *        把今天这一段收尾，第二天另起一个 00:00 → 现在 的新块（见 TimeTable.onEndRunning）。
 *        不传则取当前时刻。
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function updateTimeBlockStatus(id, status, actualEndTime) {
  const body = { executionStatus: status }
  if (actualEndTime) body.actualEndTime = actualEndTime
  return request('PUT', `/api/v1/timeline/blocks/${id}/status`, body)
}

/**
 * 查询按类别聚合的时长统计（今日 / 累计）
 *
 * 跨全部工作区统计，未分类时间块不纳入。供个人足迹生成鼓励与里程碑文案，
 * 必须实时取——历史动态里若存累计快照，过一阵就会显示过期数字。
 *
 * @returns {Promise<{code:number,message:string,data:Array<{categoryName:string,todayMinutes:number,totalMinutes:number}>,success:boolean,timestamp:string}>}
 */
export function getCategoryStats() {
  return request('GET', '/api/v1/timeline/category-stats')
}
